import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const LAST_UPDATED = '31 July 2026';
const VERSION = '1.0';
const CONTACT_EMAIL = 'info@bastions.mt';
const DPA_LINK = '#'; // TODO: replace with the live DPA URL

const DATA_COLLECTION = [
  { data: 'Identity', examples: 'Full name, job role, staff PIN or login identifier' },
  { data: 'Contact', examples: 'Email address, telephone number, business address' },
  { data: 'Business', examples: 'Restaurant trading name, VAT number, company number, number of locations/terminals' },
  { data: 'Account', examples: 'Username, hashed password, access permissions, shift and clock-in records where you enable that feature' },
  { data: 'Communications', examples: 'Support emails, feedback, bug reports and early access correspondence' },
];

const LEGAL_BASES = [
  { purpose: 'Providing the Tilly service under our agreement with you', basis: 'Contract — Art. 6(1)(b)' },
  { purpose: 'Creating and administering user accounts', basis: 'Contract — Art. 6(1)(b)' },
  { purpose: 'Providing support and responding to enquiries', basis: 'Contract — Art. 6(1)(b)' },
  { purpose: 'Keeping the service secure, preventing unauthorised access and abuse', basis: 'Legitimate interests — Art. 6(1)(f)' },
  { purpose: 'Diagnosing faults, monitoring performance, improving the product', basis: 'Legitimate interests — Art. 6(1)(f)' },
  { purpose: 'Sending service, outage and release notifications', basis: 'Legitimate interests — Art. 6(1)(f)' },
  { purpose: 'Inviting businesses to the early access programme and following up', basis: 'Legitimate interests — Art. 6(1)(f)' },
  { purpose: 'Marketing emails to people who are not yet users', basis: 'Consent — Art. 6(1)(a)' },
  { purpose: 'Responding to lawful requests from authorities', basis: 'Legal obligation — Art. 6(1)(c)' },
  { purpose: 'Establishing, exercising or defending legal claims', basis: 'Legitimate interests — Art. 6(1)(f)' },
];

const PROVIDERS = [
  {
    provider: 'Amazon Web Services EMEA SARL',
    purpose: 'Application hosting, database, object storage, backups',
    location: 'EU (eu-central-1)',
    safeguard: 'N/A — data stored in the EU',
  },
  {
    provider: 'Brevo (Sendinblue SAS)',
    purpose: 'Transactional and notification email',
    location: 'France (EU)',
    safeguard: 'N/A — EU',
  },
];

const RETENTION = [
  { data: 'Account data', period: 'For the life of the account, then 12 months after closure' },
  { data: 'Order and transaction records', period: "Retained while the restaurant's account is active, and thereafter as instructed by the restaurant" },
  { data: 'Support and feedback correspondence', period: '3 years from resolution' },
  { data: 'Server, access and audit logs', period: '12 months' },
  { data: 'Error and diagnostic records', period: '90 days' },
  { data: 'Email delivery logs (Brevo)', period: '12 months' },
  { data: 'Marketing consent records', period: 'Until withdrawn, plus 3 years as proof of consent' },
  { data: 'Backups', period: 'Overwritten on a rolling 35-day cycle' },
];

const SECTIONS = [
  { id: 'who-we-are', label: '1. Who we are' },
  { id: 'scope', label: '2. Scope' },
  { id: 'data-we-collect', label: '3. What data we collect' },
  { id: 'legal-bases', label: '4. Legal bases' },
  { id: 'how-we-use', label: '5. How we use your data' },
  { id: 'sharing', label: '6. Who we share data with' },
  { id: 'transfers', label: '7. International transfers' },
  { id: 'retention', label: '8. How long we keep data' },
  { id: 'security', label: '9. Security' },
  { id: 'your-rights', label: '10. Your rights' },
  { id: 'automated', label: '11. Automated decision-making' },
  { id: 'cookies', label: '12. Cookies and tracking' },
  { id: 'children', label: '13. Children' },
  { id: 'changes', label: '14. Changes to this policy' },
  { id: 'contact', label: '15. Contact' },
];

const PrivacyTable = ({ columns, rows }) => (
  <div className="privacy-table-wrap">
    <DataTable
      value={rows}
      className="customerTable thead-dark"
      responsiveLayout="scroll"
      showGridlines
    >
      {columns.map((col) => (
        <Column
          key={col.key}
          field={col.key}
          header={col.label}
          style={col.width ? { width: col.width } : undefined}
        />
      ))}
    </DataTable>
  </div>
);

const Privacy = () => {
  return (
    <div className="privacy-page">
      <main className="privacy-main">
        <div className="privacy-layout">
          <nav className="privacy-toc" aria-label="Table of contents">
            <p className="privacy-toc-title">Contents</p>
            <ol className="privacy-toc-list">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.label.replace(/^\d+\.\s*/, '')}</a>
                </li>
              ))}
            </ol>
          </nav>

          <article className="privacy-content">
            <header className="privacy-title-block">
              <p className="privacy-eyebrow">Legal</p>
              <h1>Privacy Policy</h1>
              <p className="privacy-meta">
                Last updated: {LAST_UPDATED}
                <span className="privacy-meta-sep">·</span>
                Version {VERSION}
              </p>
            </header>

            <section id="who-we-are" className="privacy-section">
            <h2>1. Who we are</h2>
            <p>
              Tilly is a point-of-sale (&quot;POS&quot;) application for restaurants and hospitality businesses,
              available on iOS and as a web application.
            </p>
            <p>
              Tilly is operated by Bastions View Ltd (&quot;Tilly&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;), a company registered
              in Malta with its registered office at 13, Zondadari Street, Rabat, Malta.
            </p>
            <p>
              All enquiries, including privacy and data protection requests:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
            <p>
              This policy is governed by Regulation (EU) 2016/679 (the GDPR) and the Data Protection Act
              (Chapter 586 of the Laws of Malta), together with subsidiary legislation made under it.
            </p>
            <p>
              We have not appointed a Data Protection Officer, having assessed that we do not meet the
              criteria in Article 37 GDPR. Data protection enquiries are handled at the address above.
            </p>
          </section>

          <section id="scope" className="privacy-section">
            <h2>2. Scope — and an important distinction</h2>
            <p>
              Tilly is currently available by invitation only and is not yet generally released. This
              policy applies to everyone who uses it, including participants in our early access programme.
            </p>
            <p>
              Tilly handles two very different categories of personal data, and our legal role differs for
              each. Please read this section carefully, because it determines who you should contact about
              your data.
            </p>

            <h3>2.1 Where we are the controller</h3>
            <p>We decide how and why data is processed when it concerns:</p>
            <ul>
              <li>Restaurant businesses invited to use Tilly</li>
              <li>Owners, managers, staff and other individual users who hold a Tilly account</li>
              <li>People who contact us, request access, or take part in early access and feedback</li>
            </ul>
            <p>For this data, we are the controller and this policy describes our processing in full.</p>

            <h3>2.2 Where we are a processor</h3>
            <p>
              When a restaurant uses Tilly to run its business, it may enter or generate personal data about
              its own customers, diners, delivery recipients or suppliers — for example a name on a table
              booking, a delivery address, an allergy note, or a loyalty account.
            </p>
            <p>
              For that data, the restaurant is the controller and we act only as a processor on its
              documented instructions. We do not decide what that data is used for and we do not use it for
              our own purposes.
            </p>
            <p>
              If you are a diner, guest or customer of a restaurant and you want to exercise your rights over
              your data, please contact the restaurant directly. If you contact us, we will forward your
              request to the relevant restaurant and tell you we have done so, but we cannot action it
              ourselves.
            </p>
            <p>
              Our processing on behalf of restaurants is governed by a Data Processing Agreement (DPA) that
              satisfies Article 28 GDPR, which forms part of our customer terms. It is available at{' '}
              <a href={DPA_LINK} target="_blank" rel="noopener noreferrer">
                this link
              </a>
              .
            </p>
          </section>

          <section id="data-we-collect" className="privacy-section">
            <h2>3. What data we collect</h2>

            <h3>3.1 Account and business data (we are controller)</h3>
            <PrivacyTable
              columns={[
                { key: 'data', label: 'Data', width: '25%' },
                { key: 'examples', label: 'Examples' },
              ]}
              rows={DATA_COLLECTION}
            />
            <p>We do not currently charge for Tilly and do not collect billing or payment details from our users.</p>

            <h3>3.2 Device and technical data (we are controller)</h3>
            <p>Collected automatically when you use the iOS app or web application:</p>
            <ul>
              <li>Device model, operating system version, unique device identifier (on iOS, the Identifier for Vendor / IDFV)</li>
              <li>App version and build, Flutter/framework runtime information</li>
              <li>IP address and approximate location derived from it (city/country level)</li>
              <li>Log data: timestamps, actions taken, error reports, performance and latency metrics</li>
              <li>Terminal identifier and till/location assignment</li>
            </ul>
            <p>
              These logs are generated by our own infrastructure. We do not use third-party analytics,
              advertising or attribution SDKs.
            </p>

            <h3>3.3 Transaction and operational data</h3>
            <p>
              Order lines, item selections, prices, discounts, tips, refunds and voids, table and shift
              assignments.
            </p>
            <p>
              Most of this is not personal data. It becomes personal data where it identifies a member of
              staff (for example, &quot;order processed by Maria&quot;) — for which we are controller as to the staff
              account — or where it identifies a diner — for which the restaurant is controller and we are
              processor.
            </p>

            <h3>3.4 Payments</h3>
            <p>
              Tilly does not currently process card payments and is not integrated with any payment provider.
              We do not collect, transmit or store card numbers, CVV codes, magnetic stripe or chip data at
              any point.
            </p>
            <p>
              Where a restaurant records that a payment was taken — for example marking an order as paid by
              card or cash — Tilly stores only the payment method type and the amount, entered by the
              restaurant&apos;s own staff.
            </p>

            <h3>3.5 Data we do not want</h3>
            <p>
              Please do not enter special category data (Article 9 GDPR) into free-text fields unless your
              business genuinely needs to and you have a lawful basis. Allergy and dietary notes about diners
              may constitute health data; if you record these, you are responsible as controller for having a
              valid basis and for informing the diner.
            </p>
          </section>

          <section id="legal-bases" className="privacy-section">
            <h2>4. Legal bases for processing</h2>
            <p>Where we act as controller, we rely on the following bases under Article 6(1) GDPR:</p>
            <PrivacyTable
              columns={[
                { key: 'purpose', label: 'Purpose', width: '60%' },
                { key: 'basis', label: 'Legal basis' },
              ]}
              rows={LEGAL_BASES}
            />
            <p>
              Where we rely on legitimate interests, we have carried out a balancing assessment and concluded
              that our interests do not override your rights. You may request a summary of that assessment at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
            <p>
              Where we rely on consent, you may withdraw it at any time. Withdrawal does not affect processing
              already carried out.
            </p>
            <p>
              Direct marketing is sent in accordance with S.L. 586.01 (Processing of Personal Data (Electronic
              Communications Sector) Regulations). Every message contains an unsubscribe link.
            </p>
          </section>

          <section id="how-we-use" className="privacy-section">
            <h2>5. How we use your data</h2>
            <ul>
              <li>To provide, operate and maintain the POS service</li>
              <li>To authenticate users and enforce role-based permissions</li>
              <li>To sync data across terminals and devices</li>
              <li>To provide technical support and respond to your queries</li>
              <li>To detect, investigate and prevent unauthorised access and abuse</li>
              <li>To monitor uptime, diagnose errors and improve reliability</li>
              <li>To develop new features and understand how the product is used, using aggregated or pseudonymised data wherever possible</li>
              <li>To administer the early access programme and gather feedback</li>
              <li>To comply with legal and regulatory obligations in Malta and the EU</li>
            </ul>
            <p>
              We do not sell personal data. We do not share personal data with third parties for their own
              marketing purposes. We do not use data entered by restaurants about their diners to train
              machine learning models.
            </p>
          </section>

          <section id="sharing" className="privacy-section">
            <h2>6. Who we share data with</h2>

            <h3>6.1 Sub-processors and service providers</h3>
            <p>We keep our supply chain deliberately small. We currently use:</p>
            <PrivacyTable
              columns={[
                { key: 'provider', label: 'Provider' },
                { key: 'purpose', label: 'Purpose' },
                { key: 'location', label: 'Location' },
                { key: 'safeguard', label: 'Transfer safeguard' },
              ]}
              rows={PROVIDERS}
            />
            <p>
              Each is bound by a written contract meeting Article 28 GDPR. Customers may subscribe to
              notifications of changes to this list, and may object to a new sub-processor as set out in the
              DPA.
            </p>
            <p>We do not use third-party analytics, advertising, attribution or session-recording services.</p>

            <h3>6.2 Other recipients</h3>
            <ul>
              <li>
                <strong>Professional advisers</strong> — lawyers, auditors, accountants and insurers, bound by
                professional confidentiality
              </li>
              <li>
                <strong>Authorities</strong> — the Information and Data Protection Commissioner, courts, police
                and regulators, where we are legally required to disclose
              </li>
              <li>
                <strong>Business transfers</strong> — if we are involved in a merger, acquisition or sale of
                assets, data may transfer to the acquirer, subject to this policy and with notice to affected
                users
              </li>
            </ul>
            <p>
              We do not disclose data to law enforcement without a valid legal instrument, and we will notify
              affected customers unless legally prohibited from doing so.
            </p>
          </section>

          <section id="transfers" className="privacy-section">
            <h2>7. International transfers</h2>
            <p>
              Our infrastructure and our email provider are located in the European Union, and personal data
              is stored in the EU.
            </p>
            <p>
              Some providers may have parent companies or support personnel outside the EEA. Where any
              transfer outside the EEA occurs, we rely on one or more of:
            </p>
            <ul>
              <li>An adequacy decision by the European Commission (Article 45 GDPR)</li>
              <li>Standard Contractual Clauses adopted by the European Commission (Article 46(2)(c))</li>
              <li>The EU–US Data Privacy Framework, where the recipient is certified</li>
            </ul>
            <p>
              We apply supplementary technical measures including encryption in transit and at rest. You may
              request a copy of the relevant safeguards at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </section>

          <section id="retention" className="privacy-section">
            <h2>8. How long we keep data</h2>
            <PrivacyTable
              columns={[
                { key: 'data', label: 'Data', width: '40%' },
                { key: 'period', label: 'Retention period' },
              ]}
              rows={RETENTION}
            />
            <p>
              Where we act as processor, retention is set by the restaurant. On termination we delete or return
              customer data within 30 days, except where EU or Maltese law requires us to keep it.
            </p>
            <p>
              Restaurants remain responsible for retaining their own fiscal and accounting records for the
              periods required by Maltese law. Tilly is not a system of record for that purpose, and you should
              export and retain your own copies.
            </p>
            <p>
              Anonymised and aggregated data, from which no individual can be identified, may be retained
              indefinitely.
            </p>
          </section>

          <section id="security" className="privacy-section">
            <h2>9. Security</h2>
            <p>We apply technical and organisational measures appropriate to the risk, including:</p>
            <ul>
              <li>TLS 1.2+ for all data in transit; encryption at rest for databases and backups</li>
              <li>Passwords hashed with a modern algorithm (bcrypt / Argon2id); staff PINs stored hashed, never in plaintext</li>
              <li>Secrets held in the iOS Keychain on device; no credentials stored in plaintext in local storage</li>
              <li>Role-based access control and least privilege for both customers and our own staff</li>
              <li>Multi-factor authentication for administrative and AWS production access</li>
              <li>Audit logging of privileged actions</li>
              <li>Network segregation, firewalling and regular dependency and vulnerability scanning</li>
              <li>Encrypted, tested backups with defined recovery objectives</li>
              <li>Confidentiality obligations and data protection training for all personnel</li>
            </ul>
            <p>
              No system is perfectly secure. If a personal data breach occurs, we will notify the Information
              and Data Protection Commissioner within 72 hours where required under Article 33, notify affected
              individuals without undue delay where Article 34 applies, and notify affected customers promptly
              so they can meet their own obligations as controllers.
            </p>
          </section>

          <section id="your-rights" className="privacy-section">
            <h2>10. Your rights</h2>
            <p>Under Articles 15–22 GDPR you have the right to:</p>
            <ul>
              <li>
                <strong>Access</strong> — obtain a copy of your personal data and information about how it is
                processed
              </li>
              <li>
                <strong>Rectification</strong> — have inaccurate or incomplete data corrected
              </li>
              <li>
                <strong>Erasure</strong> — have data deleted where one of the grounds in Article 17 applies
              </li>
              <li>
                <strong>Restriction</strong> — limit how we process your data in certain circumstances
              </li>
              <li>
                <strong>Portability</strong> — receive data you provided in a structured, commonly used,
                machine-readable format, and have it transmitted to another controller where technically
                feasible
              </li>
              <li>
                <strong>Object</strong> — object to processing based on legitimate interests, and object at any
                time to direct marketing (an absolute right)
              </li>
              <li>
                <strong>Withdraw consent</strong> — at any time, where processing is based on consent
              </li>
              <li>
                <strong>Not be subject to automated decisions</strong> producing legal or similarly significant
                effects
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within one month,
              extendable by two further months for complex requests, in which case we will tell you within the
              first month. We may ask for information to verify your identity. There is no charge unless a
              request is manifestly unfounded or excessive.
            </p>
            <p>Right to complain: you may lodge a complaint with the Maltese supervisory authority:</p>
            <address className="privacy-address">
              <p className="privacy-address-header">
                <i className="pi pi-shield" aria-hidden="true" />
                Information and Data Protection Commissioner (IDPC)
              </p>
              <div className="privacy-address-body">
                <div className="privacy-address-rows">
                  <div className="privacy-address-row">
                    <i className="pi pi-map-marker" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Address</span>
                      <span className="privacy-address-row-value">
                        Floor 2, Airways House, High Street, Sliema SLM 1549, Malta
                      </span>
                    </div>
                  </div>
                  <div className="privacy-address-row">
                    <i className="pi pi-phone" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Tel</span>
                      <span className="privacy-address-row-value">+356 2328 7100</span>
                    </div>
                  </div>
                  <div className="privacy-address-row">
                    <i className="pi pi-envelope" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Email</span>
                      <a href="mailto:idpc.info@idpc.org.mt" className="privacy-address-row-value">
                        idpc.info@idpc.org.mt
                      </a>
                    </div>
                  </div>
                  <div className="privacy-address-row">
                    <i className="pi pi-globe" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Web</span>
                      <a
                        href="https://idpc.org.mt"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="privacy-address-row-value"
                      >
                        idpc.org.mt
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </address>
            <p>
              You may also complain to the supervisory authority in your EU country of residence or workplace.
              We would appreciate the chance to address your concerns first.
            </p>
          </section>

          <section id="automated" className="privacy-section">
            <h2>11. Automated decision-making</h2>
            <p>
              We do not carry out automated decision-making producing legal or similarly significant effects
              concerning you, within the meaning of Article 22 GDPR.
            </p>
          </section>

          <section id="cookies" className="privacy-section">
            <h2>12. Cookies and tracking</h2>

            <h3>12.1 Web application</h3>
            <p>
              We use strictly necessary cookies only, for authentication, session management and security.
              These do not require consent under S.L. 586.01, and we do not display a cookie banner because we
              set no optional cookies.
            </p>
            <p>We do not use analytics, advertising or tracking cookies.</p>

            <h3>12.2 iOS app</h3>
            <p>
              The Tilly iOS app does not track you across apps and websites owned by other companies, and does
              not use the Advertising Identifier (IDFA). We therefore do not present an App Tracking
              Transparency prompt.
            </p>
            <p>
              Our App Store privacy labels reflect the data described in this policy. Where they differ, this
              policy governs.
            </p>
          </section>

          <section id="children" className="privacy-section">
            <h2>13. Children</h2>
            <p>
              Tilly is a business tool intended for use by people aged 16 and over in a professional capacity.
              We do not knowingly collect personal data directly from children.
            </p>
            <p>
              Where a restaurant employs staff under 16 and records them in Tilly, the restaurant is
              responsible for the lawful basis for that processing under applicable Maltese employment and data
              protection law.
            </p>
            <p>
              If we learn that we have collected data from a child in circumstances requiring parental consent
              that was not obtained, we will delete it promptly.
            </p>
          </section>

          <section id="changes" className="privacy-section">
            <h2>14. Changes to this policy</h2>
            <p>
              We may update this policy to reflect changes in our service, our providers or the law. The
              &quot;last updated&quot; date at the top will change. For material changes we will give at least 30
              days&apos; notice by email to account holders and by in-app notice before the change takes effect.
              Where a change requires your consent, we will ask for it.
            </p>
            <p>Previous versions are available on request.</p>
          </section>

          <section id="contact" className="privacy-section privacy-section-last">
            <h2>15. Contact</h2>
            <address className="privacy-address">
              <p className="privacy-address-header">
                <i className="pi pi-building" aria-hidden="true" />
                Bastions View Ltd
              </p>
              <div className="privacy-address-body">
                <div className="privacy-address-rows">
                  <div className="privacy-address-row">
                    <i className="pi pi-map-marker" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Address</span>
                      <span className="privacy-address-row-value">13, Zondadari Street, Rabat, Malta</span>
                    </div>
                  </div>
                  <div className="privacy-address-row">
                    <i className="pi pi-envelope" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Email</span>
                      <a href={`mailto:${CONTACT_EMAIL}`} className="privacy-address-row-value">
                        {CONTACT_EMAIL}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <p className="privacy-address-note">
                All enquiries, including data protection requests, can be sent to the email above.
              </p>
            </address>
          </section>
          </article>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
